"use client";

import Link from "next/link";
import { FileText, Trash2 } from "lucide-react";
import { BriefPdf, cn, formatCdnImg } from "@drpcrd/utils";
import { Button } from "../buttons/button";
import { CustomSpinner } from "../icons/custom-spinner";

type Props = {
  pdfs: BriefPdf[];
  /** Names of files still uploading, listed after the attached ones. */
  pending?: string[];
  /** When given, every attached PDF gets a remove button. */
  onRemove?: (pdf: BriefPdf) => void;
  className?: string;
};

/**
 * The PDFs attached to a brief, one row each: a file icon, the name as a
 * link that opens the file, and (while editing) a round remove button. Files
 * still uploading are listed underneath with a spinner. Renders nothing when
 * there is nothing to show, so callers can place it unconditionally.
 */
export const BriefPdfList = ({
  pdfs,
  pending = [],
  onRemove,
  className,
}: Props) => {
  if (pdfs.length === 0 && pending.length === 0) {
    return null;
  }

  return (
    <ul className={cn("flex flex-col gap-1", className)}>
      {pdfs.map((pdf) => (
        <li key={pdf.id} className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="text-primary shrink-0" size={24} aria-hidden />
            <Link
              href={{ pathname: formatCdnImg(pdf.id) }}
              target="_blank"
              download={pdf.name}
              className="text-primary truncate text-sm font-semibold hover:underline"
            >
              {pdf.name}
            </Link>
          </span>
          {onRemove && (
            <Button
              type="button"
              variant="outlined"
              icon={Trash2}
              className="size-4 shrink-0 p-0"
              aria-label={`Remove ${pdf.name}`}
              onClick={() => onRemove(pdf)}
            />
          )}
        </li>
      ))}
      {pending.map((name, index) => (
        <li
          key={`${name}-${index}`}
          className="flex items-center gap-2 text-sm text-neutral-900"
        >
          <CustomSpinner className="size-2 shrink-0" aria-hidden />
          <span className="truncate">Uploading {name}…</span>
        </li>
      ))}
    </ul>
  );
};
